// #3185 APPROVE.2 PR 2c (recon §6, ruling D8) — the partner's default client
// approval matrix, replacing the three "Require client sign-off on …" toggles
// in the Partner Portal and the onboarding wizard's step 3a. It is stored as
// `clientApprovalMatrix` ({ artefact type: required }) in the partner's
// default approval config; the three old keys are ignored and never translated
// (6426560179). It is copied into a new client's workspace defaults when the
// client is linked; Apply Defaults never changes it (D13).

import ClientApprovalMatrixTable from './ClientApprovalMatrixTable';
import PreEnforcementNotice from './PreEnforcementNotice';
import { CLIENT_APPROVABLE_TYPES, clientApprovalTypeLabel, platformDefaultRequired } from '../../lib/clientApprovals';

export type PartnerMatrix = Record<string, boolean>;

/** Every client-approvable type, from the stored matrix where it has the type, else the platform default. */
export function fullPartnerMatrix(stored: unknown): PartnerMatrix {
  const m = stored && typeof stored === 'object' && !Array.isArray(stored) ? (stored as Record<string, unknown>) : {};
  return Object.fromEntries(CLIENT_APPROVABLE_TYPES.map((t) => [t, typeof m[t] === 'boolean' ? (m[t] as boolean) : platformDefaultRequired(t)]));
}

/**
 * R1 P3-1: only the types that differ from the platform default, so a client
 * linked later gets workspace rows only where the partner chose something
 * else, and later platform-default changes still reach the rest. `{}` is valid.
 */
export function compactPartnerMatrix(full: PartnerMatrix): PartnerMatrix {
  return Object.fromEntries(Object.entries(full).filter(([t, v]) => v !== platformDefaultRequired(t)));
}

interface Props {
  value: PartnerMatrix;
  onChange: (next: PartnerMatrix) => void;
}

export default function PartnerClientApprovalMatrix({ value, onChange }: Props) {
  return (
    <>
      <PreEnforcementNotice />
      <ClientApprovalMatrixTable
        testId="partner-client-approval-matrix"
        types={CLIENT_APPROVABLE_TYPES}
        hint={(t) => `Platform default: ${platformDefaultRequired(t) ? 'required' : 'not required'}`}
        renderControl={(t) => (
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
            <input
              type="checkbox"
              aria-label={`Require client approval for ${clientApprovalTypeLabel(t)}`}
              data-testid={`partner-matrix-${t}`}
              checked={value[t] ?? platformDefaultRequired(t)}
              onChange={(e) => onChange({ ...value, [t]: e.target.checked })}
              style={{ accentColor: 'var(--primary)' }}
            />
            Required
          </label>
        )}
      />
    </>
  );
}
